/**
 * AdminStripeStatus — live ping of the Stripe API to confirm money is
 * flowing into the correct account in the correct mode.
 *
 * Why this page exists:
 *   The 2026-05-22 customer-loss incident happened because a sk_test_
 *   key leaked into production. The prod_readiness audit detects the
 *   key *prefix* — this page actually *exercises* the key against
 *   Stripe's API so we know:
 *     - the key works (auth succeeds, account retrievable)
 *     - we're in LIVE mode (not test)
 *     - charges_enabled = true on the connected account
 *     - balance is in the expected currency
 *
 *   If any of those is off, the page screams red and links to next steps.
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Navigate } from 'react-router-dom';
import {
  AlertTriangle, CheckCircle2, ExternalLink, Loader2,
  RefreshCw, ShieldAlert, ShieldCheck, XCircle,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { API } from '../utils/apiBase';
import useHideCookieBanner from '../utils/useHideCookieBanner';
import AdminReadinessBanner from '../components/AdminReadinessBanner';
import AdminSubNav from '../components/AdminSubNav';


const ModeBadge = ({ mode }) => {
  const styles = {
    live: { bg: '#ecfdf5', fg: '#065f46', label: 'LIVE MODE' },
    test: { bg: '#fef3c7', fg: '#7c2d12', label: 'TEST MODE' },
    unset: { bg: '#fef2f2', fg: '#991b1b', label: 'NOT SET' },
    unknown: { bg: '#fef2f2', fg: '#991b1b', label: 'UNKNOWN KEY' },
  };
  const s = styles[mode] || styles.unknown;
  return (
    <span
      className="inline-flex items-center px-3 py-1 text-[11px] font-bold tracking-[0.15em] uppercase"
      style={{ backgroundColor: s.bg, color: s.fg }}
      data-testid={`stripe-mode-${mode}`}
    >
      {s.label}
    </span>
  );
};


const StatusRow = ({ label, value, ok, testId, note }) => (
  <div className="flex items-center justify-between py-3 border-b border-[#e5e7eb]" data-testid={testId}>
    <div>
      <div className="text-sm font-semibold text-[#0f1115]">{label}</div>
      {note && <div className="text-[11px] text-[#6b7280] mt-0.5">{note}</div>}
    </div>
    <div className="flex items-center gap-2">
      <span className="text-sm font-mono tabular-nums text-[#0f1115]">{value}</span>
      {ok === true && <CheckCircle2 size={16} className="text-[#10b981]" />}
      {ok === false && <XCircle size={16} className="text-[#e11d48]" />}
    </div>
  </div>
);


const formatMoney = (amount, currency) => {
  if (typeof amount !== 'number') return '—';
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: (currency || 'USD').toUpperCase(),
      maximumFractionDigits: 2,
    }).format(amount);
  } catch (_e) {
    return `${currency || '$'} ${amount.toFixed(2)}`;
  }
};


const AdminStripeStatus = () => {
  const { user, loading: authLoading } = useAuth();
  useHideCookieBanner();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [err, setErr] = useState('');

  const load = useCallback(async (showSpinner = false) => {
    if (showSpinner) setLoading(true);
    else setRefreshing(true);
    setErr('');
    try {
      const r = await axios.get(`${API}/admin/stripe-status`, { withCredentials: true });
      setData(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || 'Failed to load Stripe status');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { load(true); }, [load]);

  if (authLoading) return <div className="pt-24 text-center text-[#6b7280]">Loading…</div>;
  if (!user || user.role !== 'admin') return <Navigate to="/dashboard" replace />;

  const acct = data?.account || {};
  const balance = data?.balance || { available: [], pending: [] };

  // Pre-compute headline tone
  let headline = 'Checking…';
  let headlineIcon = <Loader2 size={20} className="animate-spin text-[#6b7280]" />;
  let headlineColor = '#6b7280';
  if (data) {
    if (data.ok && data.mode === 'live' && acct.charges_enabled) {
      headline = 'Stripe is LIVE and accepting charges.';
      headlineIcon = <ShieldCheck size={20} className="text-[#10b981]" />;
      headlineColor = '#065f46';
    } else if (data.mode === 'test') {
      headline = 'Stripe is in TEST mode — real customers will not be charged.';
      headlineIcon = <ShieldAlert size={20} className="text-[#e11d48]" />;
      headlineColor = '#991b1b';
    } else if (data.mode === 'unset') {
      headline = 'STRIPE_API_KEY is not set. Checkout is disabled.';
      headlineIcon = <ShieldAlert size={20} className="text-[#e11d48]" />;
      headlineColor = '#991b1b';
    } else if (!data.ok) {
      headline = 'Stripe key set, but the API call failed. Inspect errors below.';
      headlineIcon = <AlertTriangle size={20} className="text-[#f59e0b]" />;
      headlineColor = '#b45309';
    } else if (data.ok && !acct.charges_enabled) {
      headline = 'Live key works but charges_enabled = false. Customers cannot pay.';
      headlineIcon = <AlertTriangle size={20} className="text-[#e11d48]" />;
      headlineColor = '#991b1b';
    }
  }

  return (
    <div className="min-h-screen bg-[#f4f5f7] pt-20 pb-12" data-testid="admin-stripe-status-page">
      <AdminReadinessBanner />
      <AdminSubNav />
      <div className="max-w-3xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <div>
            <span className="text-xs uppercase tracking-[0.2em] font-semibold text-[#002fa7]">ADMIN · STRIPE</span>
            <h1 className="font-satoshi text-3xl font-black text-[#0f1115]">Stripe Live Status</h1>
            <p className="text-sm text-[#6b7280]">Real ping of Stripe API · confirms mode + charges + balance</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={() => load(false)}
              disabled={refreshing}
              className="px-3 py-2 text-sm font-semibold bg-white border border-[#e5e7eb] text-[#4b5563] hover:border-[#002fa7] transition-colors inline-flex items-center gap-1.5 disabled:opacity-50"
              data-testid="stripe-status-refresh"
            >
              <RefreshCw size={14} className={refreshing ? 'animate-spin' : ''} />
              {refreshing ? 'Pinging…' : 'Re-ping Stripe'}
            </button>
          </div>
        </div>

        {/* Headline status */}
        {loading ? (
          <div className="bg-white border border-[#e5e7eb] p-8 text-center" data-testid="stripe-status-loading">
            <Loader2 size={24} className="animate-spin mx-auto text-[#6b7280]" />
            <p className="text-sm text-[#6b7280] mt-2">Pinging Stripe API…</p>
          </div>
        ) : err ? (
          <div className="bg-[#fef2f2] border-l-4 border-[#e11d48] p-4 text-sm text-[#991b1b]" data-testid="stripe-status-error">
            <AlertTriangle size={16} className="inline mr-2" />
            {err}
          </div>
        ) : data ? (
          <>
            <div
              className="bg-white border border-[#e5e7eb] p-5 mb-6 flex items-start gap-3"
              data-testid="stripe-headline"
            >
              <div className="flex-shrink-0 pt-0.5">{headlineIcon}</div>
              <div className="flex-1">
                <div className="font-satoshi text-lg font-bold leading-tight" style={{ color: headlineColor }}>
                  {headline}
                </div>
                <div className="mt-2 flex items-center gap-3 flex-wrap">
                  <ModeBadge mode={data.mode} />
                  <span className="text-xs text-[#6b7280] font-mono">{data.key_observed}</span>
                  {data.checked_at && (
                    <span className="text-[11px] text-[#9ca3af]">
                      checked {new Date(data.checked_at).toLocaleTimeString()}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Account section */}
            <section className="bg-white border border-[#e5e7eb] mb-6" data-testid="stripe-account-section">
              <h2 className="font-satoshi text-base font-bold text-[#0f1115] px-5 py-3 border-b border-[#e5e7eb]">
                Connected Account
              </h2>
              <div className="px-5">
                <StatusRow
                  label="Account ID"
                  value={acct.id || '—'}
                  ok={!!acct.id}
                  testId="stripe-row-account-id"
                />
                <StatusRow
                  label="Account email"
                  value={acct.email || '—'}
                  testId="stripe-row-account-email"
                />
                <StatusRow
                  label="Business name"
                  value={acct.business_name || '—'}
                  testId="stripe-row-business-name"
                />
                <StatusRow
                  label="Country"
                  value={acct.country || '—'}
                  testId="stripe-row-country"
                />
                <StatusRow
                  label="Default currency"
                  value={(acct.default_currency || '—').toUpperCase()}
                  testId="stripe-row-currency"
                />
                <StatusRow
                  label="Charges enabled"
                  value={acct.charges_enabled ? 'YES' : 'NO'}
                  ok={!!acct.charges_enabled}
                  testId="stripe-row-charges-enabled"
                  note="If NO, complete your Stripe onboarding before accepting cards."
                />
                <StatusRow
                  label="Payouts enabled"
                  value={acct.payouts_enabled ? 'YES' : 'NO'}
                  ok={!!acct.payouts_enabled}
                  testId="stripe-row-payouts-enabled"
                  note="If NO, you can take payments but funds will pool — fix bank info."
                />
                {/* `livemode` is null on Account.retrieve — Stripe only sets it
                    on individual API objects like Charges. Only render the row
                    when Stripe actually returned a boolean so we don't show a
                    misleading red X when everything else is live + healthy. */}
                {typeof acct.livemode === 'boolean' && (
                  <StatusRow
                    label="Live mode flag"
                    value={acct.livemode ? 'LIVE' : 'TEST'}
                    ok={acct.livemode === true}
                    testId="stripe-row-livemode"
                  />
                )}
              </div>
            </section>

            {/* Balance section */}
            <section className="bg-white border border-[#e5e7eb] mb-6" data-testid="stripe-balance-section">
              <h2 className="font-satoshi text-base font-bold text-[#0f1115] px-5 py-3 border-b border-[#e5e7eb]">
                Balance
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 divide-y sm:divide-y-0 sm:divide-x divide-[#e5e7eb]">
                <div className="px-5 py-4">
                  <div className="text-[10px] uppercase tracking-wider text-[#6b7280] font-semibold mb-2">
                    Available
                  </div>
                  {(balance.available || []).length === 0 ? (
                    <div className="text-sm text-[#9ca3af]" data-testid="stripe-balance-available-empty">No balance data.</div>
                  ) : balance.available.map((b) => (
                    <div
                      key={`avail-${b.currency}`}
                      className="font-satoshi text-2xl font-black text-[#10b981] mb-1"
                      data-testid={`stripe-balance-available-${b.currency}`}
                    >
                      {formatMoney(b.amount, b.currency)}
                    </div>
                  ))}
                </div>
                <div className="px-5 py-4">
                  <div className="text-[10px] uppercase tracking-wider text-[#6b7280] font-semibold mb-2">
                    Pending
                  </div>
                  {(balance.pending || []).length === 0 ? (
                    <div className="text-sm text-[#9ca3af]" data-testid="stripe-balance-pending-empty">No pending funds.</div>
                  ) : balance.pending.map((b) => (
                    <div
                      key={`pend-${b.currency}`}
                      className="font-satoshi text-2xl font-black text-[#002fa7] mb-1"
                      data-testid={`stripe-balance-pending-${b.currency}`}
                    >
                      {formatMoney(b.amount, b.currency)}
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {/* Errors */}
            {Array.isArray(data.errors) && data.errors.length > 0 && (
              <section className="bg-[#fef2f2] border-l-4 border-[#e11d48] p-4" data-testid="stripe-errors">
                <div className="text-sm font-bold text-[#991b1b] mb-2">
                  <AlertTriangle size={14} className="inline mr-1.5" />
                  Stripe API returned errors
                </div>
                <ul className="text-xs text-[#7f1d1d] space-y-1 font-mono">
                  {data.errors.map((e, i) => (
                    <li key={i} data-testid={`stripe-error-${i}`}>· {e}</li>
                  ))}
                </ul>
              </section>
            )}

            {/* Help links */}
            <div className="mt-6 text-xs text-[#6b7280] flex items-center gap-4 flex-wrap">
              <a
                href="https://dashboard.stripe.com/balance"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 hover:text-[#002fa7]"
                data-testid="stripe-dashboard-link"
              >
                Open Stripe Dashboard <ExternalLink size={11} />
              </a>
              <a
                href="https://dashboard.stripe.com/apikeys"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 hover:text-[#002fa7]"
                data-testid="stripe-apikeys-link"
              >
                API keys <ExternalLink size={11} />
              </a>
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
};

export default AdminStripeStatus;
